import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { Controller, FieldError, FormProvider, Resolver, useForm, useFormContext } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';

import { Editor, RichMediaInput, RichMediaState, useRichInput } from '@bonusly/common-action-input';
import { RelativeTime } from '@bonusly/common-relative-time';
import { RichTextDisplay, RichTextInput, useRichText } from '@bonusly/common-rich-text-editor';
import { UserPopover } from '@bonusly/common-user-popover';
import {
	Alert,
	AlertBody,
	AlertDescription,
	AlertIcon,
	ArrowLeftOutline,
	Avatar,
	Box,
	Button,
	Center,
	CheckCircleSolid,
	DocumentTextOutline,
	FormControl,
	FormHelperText,
	forwardRef,
	Grid,
	Heading,
	HStack,
	IconButton,
	Modal,
	ModalBody,
	ModalCloseButton,
	ModalContent,
	ModalFooter,
	ModalHeader,
	ModalOverlay,
	Radio,
	Spacer,
	Stack,
	Text,
	Tooltip,
	useToast,
	VStack,
} from '@bonusly/design-system';
import { toHttpError } from '@bonusly/http';
import { Recipient } from '@bonusly/recipient-entities';
import { assertPresence } from '@bonusly/runtime';
import { Schema } from '@bonusly/schema';
import { useAnalytics, useCurrentUser } from '@bonusly/who-am-i';

import { InfoLabel } from '../../components/form/info-label';
import { UserNominationPeriod } from '../../entities/nominations';
import { loadUserGroups, useUserGroups } from '../../queries/groups';
import {
	loadUserNominationPeriods,
	RuleCondition,
	ruleConditionAttrToRuleOptionsGroup,
	useCreateNomination,
	useInvalidateNominationPeriods,
	useUserNominationPeriods,
} from '../../queries/nominations';
import { loadSearchUsers } from '../../queries/users';
import { UserNominationTrackingEvent } from './events';
import { useNominationModal } from './hooks';

enum Stage {
	Select,
	Submit,
	History,
}

type FormState = {
	nominationPeriodId: string;
	nominatedUserId: string;
	reason: string;
};

const defaultFormState: FormState = {
	nominationPeriodId: '',
	nominatedUserId: '',
	reason: '',
};

const resolver: Resolver<FormState> = (values: FormState) => {
	let errors = Object.entries(values).reduce<Record<string, FieldError>>((acc, [key, value]) => {
		if (value.trim()) {
			return acc;
		}

		return {
			...acc,
			[key]: {
				type: 'required',
				// This is never shown in the UI so no translation is needed
				message: 'This field is required',
			},
		};
	}, {});

	return { values, errors };
};

export const NominationModal: FC = () => {
	const { track } = useAnalytics();
	const { t } = useTranslation('nominations', { keyPrefix: 'modal' });
	const currentUser = useCurrentUser();
	const [stage, setStage] = useState(Stage.Select);
	const form = useForm({ defaultValues: defaultFormState, resolver });
	const toast = useToast();
	const modal = useNominationModal();

	// The fetchRecipients handler passed to the useRichInput hook is created only once, so we
	// need to include a ref object in the closure with the ID of the current nomination period.
	const periodId = form.watch('nominationPeriodId');
	const periodIdRef = useRef(periodId);
	periodIdRef.current = periodId;

	const { data: periods = [] } = useUserNominationPeriods();
	const period = useMemo(() => periods.find((p) => p.id === periodId), [periodId]);

	const input = useRichInput({
		editable: !period || period.canSubmitNomination,
		modules: ['recipient'],
		fetchRecipients: async ({ query }: { query: string }) => {
			const periods = await loadUserNominationPeriods();
			const selected = periods.find((p) => p.id === periodIdRef.current);

			assertPresence(selected);

			const nonSelectableUserIds = selected.currentUserNominations.map((n) => n.nominatedUserId).concat(currentUser.id);
			const rules = selected.nomineeRulesGroup;
			const conditions = rules ? Schema.serialize<RuleCondition[]>(rules.conditions) : [];

			// This query is cached so similar searches on the same nomination period will not trigger
			// a new request to the server.
			const users = await loadSearchUsers({ search: query, conditions, operator: 'or' });

			// We need to convert the returned data from the User schema to the Recipient object
			// that is usable by the RichMediaInput component.
			return users
				.filter((u) => !nonSelectableUserIds.includes(u.id))
				.map((user) => {
					return new Recipient({
						id: user.id,
						display_name: user.displayName,
						username: user.username,
						profile_pic_url: user.profilePic,
					});
				});
		},
		options: {
			recipient: {
				allowMultiple: false,
				matchWithoutTrigger: true,
			},
		},
	});

	// I don't like doing updates in useEffects but it's really the only way to accurrately do it with Prosemirror
	useEffect(() => {
		if (period && !period.canSubmitNomination) {
			input.editor?.setEditable(false);
		} else {
			input.editor?.setEditable(true);
		}

		input.editor?.commands.setContent('');
	}, [period]);

	// The editor has to manage it's own state because the recipient is actually an object representing the
	// user. So, if the value of the selected user ID ever changes in the editor, we need to update the form
	// manually.
	const data = RichMediaState.parse(input.editor);
	const userId = data.recipients[0]?.id ?? '';
	const formUserId = form.watch('nominatedUserId');
	if (userId !== formUserId) {
		form.setValue('nominatedUserId', userId);
		form.trigger('nominatedUserId');
	}

	useEffect(() => {
		return useNominationModal.subscribe(async (state) => {
			if (state.id) {
				const periods = await loadUserNominationPeriods();
				const nominationPeriod = periods.find((period) => period.id === state.id);

				if (nominationPeriod) {
					form.setValue('nominationPeriodId', state.id);
					setStage(Stage.Submit);
				}
			}

			if (state.isOpen) {
				loadUserGroups();
			}
		});
	}, []);

	const handleReset = () => {
		setStage(Stage.Select);
		input.editor?.commands.setContent('');
		form.reset();
	};

	const invalidate = useInvalidateNominationPeriods();
	const { mutate, isLoading } = useCreateNomination({
		onSuccess: () => {
			toast({
				title: t('successMessage'),
				status: 'success',
			});
			modal.onClose();
		},
		onError: (e) => {
			if (toHttpError(e).isConflict) {
				// If it's a conflict error it means the user has already submitted a nomination for this period.
				// In that case let's close the modal and invalidate the query store since the most likely reason
				// is that a nomination was made outside of this modal while it was open
				toast({
					title: t('errorConflict'),
					status: 'error',
				});

				invalidate();
				modal.onClose();
			} else {
				toast({
					title: t('errorGeneric'),
					status: 'error',
				});
			}
		},
	});

	const handleOnClickNext = () => {
		setStage(Stage.Submit);
		track(UserNominationTrackingEvent.SelectNominationPeriod);
	};

	return (
		<Modal isOpen={modal.isOpen} onClose={modal.onClose} closeOnEsc={!isLoading} onCloseComplete={handleReset} scrollBehavior="inside" size="2xl">
			<ModalOverlay />
			<ModalContent as="form" h="full" onSubmit={form.handleSubmit((data) => mutate(data))}>
				<FormProvider {...form}>
					{stage === Stage.Select && <SelectNominationPeriod onClickNext={handleOnClickNext} periods={periods} />}
					{stage === Stage.Submit && (
						<SubmitNomination
							onClickBack={() => setStage(Stage.Select)}
							onClickViewHistory={() => setStage(Stage.History)}
							isLoading={isLoading}
							period={period}
							{...input}
						/>
					)}
					{stage === Stage.History && <ViewHistory onClickBack={() => setStage(Stage.Submit)} period={period} />}
				</FormProvider>
			</ModalContent>
		</Modal>
	);
};

type NominationPeriodProps = {
	onClickNext: () => void;
	periods: UserNominationPeriod[];
};

const SelectNominationPeriod: FC<NominationPeriodProps> = ({ onClickNext, periods }) => {
	const { t } = useTranslation('nominations', { keyPrefix: 'modal' });
	const { watch } = useFormContext();
	const nominationPeriodId = watch('nominationPeriodId');
	// Pre-load rules for potential use in submit
	useUserGroups();

	return (
		<>
			<ModalHeader>
				<Text as="h2" fontSize="lg" fontWeight="semibold">
					{t('submitTitle')}
				</Text>
				<ModalCloseButton />
			</ModalHeader>

			<ModalBody px={0} py={5}>
				<VStack>
					{periods.map((period) => {
						const names = period.currentUserNominations.map((n) => n.nominatedUser.displayName).sort();

						return (
							<Grid
								as="label"
								borderBottom="1px solid"
								borderColor="border.default"
								columnGap={3}
								cursor="pointer"
								key={period.id}
								rowGap={1.5}
								px={5}
								py={4}
								templateColumns="1.5rem 1fr"
								templateRows="1.75rem 1fr"
								w="full"
							>
								<Box pt={1}>
									<Controller name="nominationPeriodId" render={({ field }) => <RadioButton {...field} id={period.id} value={period.id} />} />
								</Box>

								<HStack>
									<Text as="h3" fontSize="lg" fontWeight="medium">
										{period.name}
									</Text>
								</HStack>

								<Box />

								<Stack gap={1}>
									<Text>{period.description}</Text>

									<HStack spacing={1}>
										{period.endDate && (
											<Text size="sm" color="text.softer">
												{t('submitBy', { date: period.endDate })}
											</Text>
										)}

										{period.currentUserNominations.length > 0 && (
											<Tooltip
												hasArrow
												label={t('completeNominationTooltip', {
													count: period.currentUserNominations.length,
													date: period.currentUserNominations[0].createdAt,
													names: names,
												})}
											>
												<Center>
													<CheckCircleSolid color="primary.fg" size="sm" />
												</Center>
											</Tooltip>
										)}
									</HStack>
								</Stack>
							</Grid>
						);
					})}
				</VStack>
			</ModalBody>

			<ModalFooter>
				<Button isDisabled={!nominationPeriodId} onClick={onClickNext} type="button">
					{t('next')}
				</Button>
			</ModalFooter>
		</>
	);
};

export const RadioButton = forwardRef((props, ref) => {
	const { watch } = useFormContext();
	const isChecked = watch('nominationPeriodId') === props.value;

	return <Radio ref={ref} isChecked={isChecked} {...props} />;
});

type SubmitNominationProps = {
	editor: Editor | null;
	isLoading: boolean;
	onClickBack: () => void;
	onClickViewHistory: () => void;
	period?: UserNominationPeriod;
	uniqueId: string;
};

const SubmitNomination: FC<SubmitNominationProps> = ({ editor, isLoading, onClickBack, onClickViewHistory, period, uniqueId }) => {
	const { track } = useAnalytics();
	const { t } = useTranslation('nominations', { keyPrefix: 'modal' });
	const { formState, watch, setValue } = useFormContext();
	const { data: rules } = useUserGroups();

	assertPresence(period);

	const { context, items } = useMemo(() => {
		const def = { context: null, items: [] };

		if (!period.nomineeRulesGroup) {
			return def;
		}

		const condition = period.nomineeRulesGroup.conditions[0];

		if (!condition) {
			return def;
		}

		if (condition.attr === 'has_reports?') {
			return { context: condition.value === 'true' ? 'isManager' : 'isNotManager', items: [] };
		}

		const context = ruleConditionAttrToRuleOptionsGroup[condition.attr];
		const rulesInContext = rules?.[context] ?? [];
		const items = period.nomineeRulesGroup.conditions.map((c) => {
			return rulesInContext.find((r) => r.key == c.value)?.name ?? c.value;
		});

		return {
			context,
			items: items,
		};
	}, []);

	const { editor: reasonEditor, html: reasonHTML } = useRichText({
		placeholder: t('reasonPlaceholder'),
		content: '',
		editorProps: {
			attributes: {
				'data-testid': 'user-nominate-reason',
			},
		},
	});

	const reason = watch('reason');

	useEffect(() => {
		if (reason !== reasonHTML) {
			setValue('reason', reasonHTML, {
				shouldValidate: !!reason,
			});
		}
	}, [reason, reasonHTML, setValue]);

	return (
		<>
			<ModalHeader display="flex" alignItems="center" gap={2}>
				<IconButton
					aria-label={t('backLabel')}
					bgColor="grayAlpha.100"
					borderRadius="full"
					onClick={onClickBack}
					size="sm"
					_hover={{
						bgColor: 'grayAlpha.150',
					}}
				>
					<ArrowLeftOutline size="sm" />
				</IconButton>

				<Text as="h2" fontSize="lg" fontWeight="semibold">
					{t('submitTitle')}
				</Text>

				<ModalCloseButton />
			</ModalHeader>

			<ModalBody p={5} display="flex" flexDir="column">
				<Stack gap={4} flexGrow={1}>
					{!period.canSubmitNomination && (
						<Alert size="sm">
							<AlertIcon />
							<AlertBody>
								<AlertDescription>{t('maxNominationsAlert')}</AlertDescription>
							</AlertBody>
						</Alert>
					)}
					<HStack>
						<Heading size="lg">{period.name}</Heading>
						<Tooltip label={period.description} color="white">
							<IconButton
								bg="transparent"
								aria-label={t('descriptionButton')}
								_hover={{
									bg: 'grayAlpha.200',
								}}
								onClick={(e) => {
									e.preventDefault();
									e.stopPropagation();
								}}
							>
								<DocumentTextOutline />
							</IconButton>
						</Tooltip>
					</HStack>
					<FormControl isDisabled={!period.canSubmitNomination}>
						<InfoLabel label={t('nomineeTooltip')}>{t('nomineeLabel')}</InfoLabel>
						<RichMediaInput
							border="1px solid"
							borderColor={period.canSubmitNomination ? 'inherit' : 'gray.100'}
							borderRadius="md"
							cursor={period.canSubmitNomination ? 'text' : 'not-allowed'}
							editor={editor}
							fontSize="lg"
							uniqueId={uniqueId}
						/>
						{context && (
							<FormHelperText>
								<Trans t={t} i18nKey="nomineeHelp" values={{ context, items }} shouldUnescape={true}>
									Nominate any user in
									<Text as="span" size="sm">
										{{ items } as any}
									</Text>
									locations.
								</Trans>
							</FormHelperText>
						)}
					</FormControl>

					<FormControl isDisabled={!period.canSubmitNomination} display="flex" flexDir="column" flexGrow={1}>
						<InfoLabel label={t('reasonTooltip')}>{t('reasonLabel')}</InfoLabel>
						<RichTextInput editor={reasonEditor} fontSize="sm" display="flex" flexDir="column" flexGrow={1} />
					</FormControl>
				</Stack>
			</ModalBody>

			<ModalFooter>
				{period.currentUserNominations.length > 0 && (
					<Button onClick={onClickViewHistory} variant="link">
						{t('historyLabel')}
					</Button>
				)}
				<Spacer />
				<Button isDisabled={!formState.isValid} isLoading={isLoading} type="submit" onClick={() => track(UserNominationTrackingEvent.SubmitNomination)}>
					{t('submit')}
				</Button>
			</ModalFooter>
		</>
	);
};

type ViewHistoryProps = {
	onClickBack: () => void;
	period?: UserNominationPeriod;
};

const ViewHistory: FC<ViewHistoryProps> = ({ onClickBack, period }) => {
	const { t } = useTranslation('nominations', { keyPrefix: 'modal' });

	assertPresence(period);

	return (
		<>
			<ModalHeader display="flex" alignItems="center" gap={2}>
				<IconButton
					aria-label={t('backLabel')}
					bgColor="grayAlpha.100"
					borderRadius="full"
					onClick={onClickBack}
					size="sm"
					_hover={{
						bgColor: 'grayAlpha.150',
					}}
				>
					<ArrowLeftOutline size="sm" />
				</IconButton>

				<Text as="h2" fontSize="lg" fontWeight="semibold">
					{t('historyTitle')}
				</Text>

				<ModalCloseButton />
			</ModalHeader>

			<ModalBody p={5}>
				<Stack gap={4}>
					<Heading size="lg">{period.name}</Heading>

					{period.currentUserNominations.map((nomination) => {
						return (
							<Stack
								key={nomination.id}
								role="article"
								bg="white"
								borderColor="border.default"
								borderWidth="1px"
								borderStyle="solid"
								borderRadius="lg"
								p={5}
								spacing={4}
							>
								<HStack>
									<UserPopover userId={nomination.nominatedUser.id}>
										<HStack bgColor="secondary.bgAlpha" borderRadius="full" p={2} pr={4} gap={2} cursor="pointer">
											<Avatar size="sm" name={nomination.nominatedUser.displayName} src={nomination.nominatedUser.profilePic} />
											<Text as="span" fontWeight="medium">
												{nomination.nominatedUser.displayName}
											</Text>
										</HStack>
									</UserPopover>
									<Spacer />
									<RelativeTime date={nomination.createdAt} fontSize="sm" color="text.softer" />
								</HStack>

								<RichTextDisplay>{nomination.reason}</RichTextDisplay>
							</Stack>
						);
					})}
				</Stack>
			</ModalBody>
		</>
	);
};
